@let items = sortedItems();

@switch (render()) {
    @case ('bullet') {
        <ul
            class="mb-0 grid gap-4 tracking-[0.5px]"
            [class]="columnClass">
            @for (item of items; track item.id) {
                <li
                    [innerHTML]="item.text | nl2br | sanitize: securityContext"
                    class="before:bg-black dark:before:bg-white relative pl-3 before:absolute before:top-[0.8rem] before:left-0 before:h-1 before:w-1 before:-translate-y-1/2 before:rounded-full before:content-['']"></li>
            }
        </ul>
    }
    @case ('bullet-bold') {
        <ul
            class="mb-0 grid gap-4 tracking-[0.5px]"
            [class]="columnClass">
            @for (item of items; track item.id) {
                <li
                    class="before:bg-black dark:before:bg-white relative pl-3 before:absolute before:top-[0.8rem] before:left-0 before:h-1 before:w-1 before:-translate-y-1/2 before:rounded-full before:content-['']">
                    @if (item.title) {
                        <strong
                            class="mr-1"
                            [innerHtml]="item.title + ':' | nl2br | sanitize: securityContext"></strong>
                    }
                    <span [innerHTML]="item.text | nl2br | sanitize: securityContext"></span>
                </li>
            }
        </ul>
    }
    @case ('bullet-bold-heading') {
        <ul
            class="mb-0 grid gap-4 tracking-[0.5px]"
            [class]="columnClass">
            @for (item of items; track item.id) {
                <li
                    class="before:bg-black dark:before:bg-white relative pl-3 before:absolute before:top-[0.8rem] before:left-0 before:h-1 before:w-1 before:-translate-y-1/2 before:rounded-full before:content-['']">
                    @if (item.title) {
                        <strong class="mr-1 block">
                            {{ item.title }}
                        </strong>
                    }
                    <span
                        class="block"
                        [innerHTML]="item.text | nl2br | sanitize: securityContext"></span>
                </li>
            }
        </ul>
    }
    @case ('checkmark') {
        <ul
            class="mb-0 grid gap-4 tracking-[0.5px]"
            [class]="columnClass">
            @for (item of items; track item.id) {
                <li>
                    <span class="flex">
                        <span class="mr-2 w-4 grow-0 translate-y-1">
                            <big-icon
                                [icon]="iconOk"
                                [size]="'w-4'"
                                class="fill-primary dark:fill-white" />
                        </span>
                        <span [innerHTML]="item.text | nl2br | sanitize: securityContext"></span>
                    </span>
                </li>
            }
        </ul>
    }
    @case ('checkmark-heading') {
        <ul
            class="mb-0 grid gap-4 tracking-[0.5px]"
            [class]="columnClass">
            @for (item of items; track item.id) {
                <li>
                    <div class="flex items-start">
                        <span class="flex-shrink-0 block w-5 h-5 rounded-full bg-primary-500 dark:bg-white relative">
                            <big-icon
                                [icon]="iconOk"
                                [size]="'w-3'"
                                [inline]="true"
                                class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 fill-white dark:fill-primary-500" />
                        </span>

                        <span class="ml-2">
                            <h4
                                class="h6 mb-1 leading-normal"
                                [class.hidden]="!item.title"
                                [class.block]="item.title"
                                [innerHtml]="item.title ?? '' | nl2br | sanitize: securityContext"></h4>

                            <span
                                class="block"
                                [innerHTML]="item.text | nl2br | sanitize: securityContext"></span>

                            @if (item.link && item.link.href && item.link.title) {
                                <span class="flex pt-4">
                                    <big-ui-link
                                        [linkClass]="'tertiary dark:text-white flex items-center gap-2'"
                                        [href]="item.link.href"
                                        [target]="item.link.target">
                                        <span class="leading-normal">{{ item.link.title }}</span>
                                        <big-icon
                                            [icon]="iconArrowRight"
                                            [size]="'w-5'"
                                            [inline]="true"
                                            class="fill-current" />
                                    </big-ui-link>
                                </span>
                            }
                        </span>
                    </div>
                </li>
            }
        </ul>
    }
    @case ('circle-checkmark-heading') {
        <ul
            class="mb-0 grid gap-4 tracking-[0.5px]"
            [class]="columnClass">
            @for (item of items; track item.id) {
                <li>
                    <div class="flex items-start">
                        <span class="flex-shrink-0 block w-5 h-5 rounded-full bg-primary-500 dark:bg-white relative">
                            <big-icon
                                [icon]="iconOk"
                                [size]="'w-3'"
                                [inline]="true"
                                class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 fill-white dark:fill-primary-500" />
                        </span>

                        <span class="ml-2">
                            @if (item.title) {
                                <h4 class="h6 block mb-1 leading-normal">
                                    {{ item.title }}
                                </h4>
                            }
                            <span
                                class="block"
                                [innerHTML]="item.text | nl2br | sanitize: securityContext"></span>
                        </span>
                    </div>
                </li>
            }
        </ul>
    }
    @case ('counter') {
        <ol
            class="mb-0 grid gap-x-4 gap-y-6 tracking-[0.5px]"
            [class]="columnClass">
            @for (item of items; let i = $index; track item.id) {
                <li class="relative pl-8">
                    <span
                        class="absolute border-primary-500 leading-tight border-2 font-bold flex h-6 items-center justify-center left-0 rounded-full text-primary-500 w-6 dark:border-primary-50 dark:text-primary-50">
                        {{ i + 1 }}
                    </span>
                    <h4
                        class="h6 mb-1 leading-normal"
                        [class.hidden]="!item.title"
                        [class.block]="!!item.title"
                        [innerHtml]="item.title ?? '' | nl2br | sanitize: securityContext"></h4>
                    <span [innerHTML]="item.text | nl2br | sanitize: securityContext"></span>

                    @if (item.link && item.link.href && item.link.title) {
                        <span class="flex pt-4">
                            <big-ui-link
                                [linkClass]="'tertiary text-primary dark:text-white flex items-center gap-2'"
                                [href]="item.link.href"
                                [target]="item.link.target">
                                <span class="leading-normal">{{ item.link.title }}</span>
                                <big-icon
                                    [icon]="iconArrowRight"
                                    [size]="'w-5'"
                                    [inline]="true"
                                    class="fill-current" />
                            </big-ui-link>
                        </span>
                    }

                    @if (item.file) {
                        <span class="flex pt-4">
                            <big-ui-link
                                [linkClass]="'tertiary text-primary dark:text-white flex items-center gap-2'"
                                [href]="item.file.uri"
                                [download]="item.file.title + '.' + item.file.type">
                                <span>{{ item.fileTitle ?? item.file.title + '.' + item.file.type }}</span>
                                <big-icon
                                    [icon]="iconDownload"
                                    [size]="'w-5'"
                                    [inline]="true"
                                    class="fill-current" />
                            </big-ui-link>
                        </span>
                    }
                </li>
            }
        </ol>
    }
}
